import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoPopup from "../../../../common/components/info-popup/info-popup";
import "./service-packages.scss";

interface ServiceItem {
    name: string;
    is_mapped: boolean;
}

interface Package {
    id: number;
    name: string;
    cost: string;
    description: string;
    items: ServiceItem[];
    isActive: boolean;
}

interface ServicePackagesProps {
    data: Package[] | any;
    refresh?: () => void;
}

const PACKAGE_DISPLAY_ORDER = ["basic", "standard", "premium"];

const ServicePackages: React.FC<ServicePackagesProps> = ({ data, refresh }) => {
    const rawPackages: Package[] = Array.isArray(data) ? data : [];
    const packages = useMemo(() => {
        return [...rawPackages].sort((a, b) => {
            const orderA = PACKAGE_DISPLAY_ORDER.indexOf((a.name || "").toLowerCase());
            const orderB = PACKAGE_DISPLAY_ORDER.indexOf((b.name || "").toLowerCase());
            const idxA = orderA === -1 ? 999 : orderA;
            const idxB = orderB === -1 ? 999 : orderB;
            return idxA - idxB;
        });
    }, [data]);

    useEffect(() => {
        // Data processing if needed
    }, [data]);

    const [infoPopup, setInfoPopup] = useState({
        open: false,
        title: "",
        note: "",
    });

    return (
        <Box className="service-packages">
            <Box className="service-packages__header">
                <Typography variant="h5" className="service-packages__title">
                    Select Service Packages
                </Typography>
                <Typography variant="body1" className="service-packages__subtitle">
                    Choose the service packages you want to offer to your clients. You can select multiple packages.
                </Typography>
            </Box>

            <Box className="service-packages__list">
                <Typography variant="h6" className="service-packages__list-title">
                    Available Service Packages
                </Typography>
                <Typography variant="body2" className="service-packages__list-subtitle">
                    Select the packages you want to offer to your clients
                </Typography>

                {packages.map((pkg) => (
                    <Box key={pkg.id} className="service-packages__item">
                        <Accordion elevation={0} className="service-packages__accordion">
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                className="service-packages__accordion-summary"
                            >
                                <Box className="service-packages__accordion-header">
                                    <Box className="service-packages__pkg-info">
                                        <Box className="service-packages__pkg-name-row" mt={1} mb={1}>
                                            <Typography variant="h6" className="service-packages__pkg-name">
                                                {pkg.name}
                                            </Typography>
                                            <Typography variant="h6" className="service-packages__pkg-price">
                                                ${pkg.cost}
                                            </Typography>
                                            <span
                                                className={`status-badge ${pkg.isActive ? "active" : "inactive"}`}
                                            >
                                                {pkg.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </Box>
                                        <Typography variant="body2" className="service-packages__pkg-desc">
                                            {pkg.description}
                                        </Typography>
                                        <Box className="service-packages__pkg-tags">
                                            {pkg.items.filter(i => i.is_mapped).map((item, idx) => (
                                                <Typography key={idx} className="service-packages__tag">
                                                    {item.name}
                                                </Typography>
                                            ))}
                                            {pkg.items.filter(i => !i.is_mapped).length > 0 && (
                                                <Typography className="service-packages__tag service-packages__tag--missing">
                                                    +{pkg.items.filter(i => !i.is_mapped).length} not included
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails className="service-packages__accordion-details">
                                <Typography variant="subtitle1" className="service-packages__include-title">
                                    Services Include
                                </Typography>
                                <Grid container spacing={2}>
                                    {pkg.items.map((service, idx) => (
                                        <Grid item xs={12} sm={6} key={idx} className="service-packages__service-item">
                                            {service.is_mapped ? (
                                                <CheckIcon className="service-packages__icon service-packages__icon--check" />
                                            ) : (
                                                <CloseIcon className="service-packages__icon service-packages__icon--close" />
                                            )}
                                            <Typography
                                                variant="body2"
                                                className={`service-packages__service-name ${!service.is_mapped ? "service-packages__service-name--excluded" : ""
                                                    }`}
                                            >
                                                {service.name}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                ))}
            </Box>



            <InfoPopup
                open={infoPopup.open}
                title={infoPopup.title}
                note={infoPopup.note}
                onClose={() => setInfoPopup((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
};

export default ServicePackages;

